// Géocodage et calcul de distance routière depuis l'agence Cygnature
// Aucune clé API requise (Nominatim + OSRM)

export const AGENCE_CYGNATURE = {
  label: '8 Rue du Champ de la Gaule, 10120 Saint-Pouange',
  lat: 48.2258075,
  lon: 4.0406204,
};

export interface Coords {
  lat: number;
  lon: number;
}

export interface NominatimSuggestion {
  display_name: string;
  lat: string;
  lon: string;
  place_id: number;
}

/** Distance à vol d'oiseau (fallback si OSRM indisponible) */
export function haversineKm(a: Coords, b: Coords) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Recherche d'adresses — API unifiée pour tout le CRM.
 * Utilise l'API Adresse data.gouv.fr (BAN) pour des résultats cohérents
 * avec les composants AddressAutocomplete/CityAutocomplete.
 * Retourne le format NominatimSuggestion pour rétro-compatibilité.
 */
export async function searchAddress(query: string, limit = 6): Promise<NominatimSuggestion[]> {
  if (query.trim().length < 3) return [];
  const suggestions = await searchAddressGouv(query, limit);
  return suggestions.map((s, idx) => ({
    display_name: s.label,
    lat: String(s.lat),
    lon: String(s.lon),
    place_id: idx,
  }));
}

/** Suggestion BAN (API Adresse gouv) — gratuit, sans clé, ultra-rapide pour la France */
export interface AdresseGouvSuggestion {
  label: string;        // "8 Rue du Champ de la Gaule 10120 Saint-Pouange"
  housenumber?: string;
  street?: string;
  postcode?: string;
  city?: string;
  context?: string;     // "10, Aube, Grand Est"
  citycode?: string;
  lat: number;
  lon: number;
}

/** Recherche d'adresses via l'API Adresse data.gouv.fr (BAN). Aucune clé requise. */
export async function searchAddressGouv(query: string, limit = 6): Promise<AdresseGouvSuggestion[]> {
  if (query.trim().length < 3) return [];
  const url = `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=${limit}&autocomplete=1`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('API Adresse indisponible');
  const data = await res.json();
  return (data?.features || []).map((f: any) => {
    const p = f.properties || {};
    const [lon, lat] = f.geometry?.coordinates || [0, 0];
    return {
      label: p.label,
      housenumber: p.housenumber,
      street: p.street || p.name,
      postcode: p.postcode,
      city: p.city,
      context: p.context,
      citycode: p.citycode,
      lat,
      lon,
    } as AdresseGouvSuggestion;
  });
}

/** Recherche uniquement de communes (villes) via l'API BAN — utile quand on n'a pas d'adresse précise */
export async function searchCityGouv(query: string, limit = 6): Promise<AdresseGouvSuggestion[]> {
  if (query.trim().length < 2) return [];
  const url = `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=${limit}&type=municipality&autocomplete=1`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('API Adresse indisponible');
  const data = await res.json();
  return (data?.features || []).map((f: any) => {
    const p = f.properties || {};
    const [lon, lat] = f.geometry?.coordinates || [0, 0];
    return {
      label: p.label,
      postcode: p.postcode,
      city: p.city || p.name,
      context: p.context,
      citycode: p.citycode,
      lat,
      lon,
    } as AdresseGouvSuggestion;
  });
}

/** Géocodage rapide : retourne lat/lon de la première suggestion (ou null) */
export async function geocodeAddress(query: string): Promise<(Coords & { display_name: string }) | null> {
  const results = await searchAddress(query, 1);
  if (!results.length) return null;
  const r = results[0];
  return { lat: parseFloat(r.lat), lon: parseFloat(r.lon), display_name: r.display_name };
}

/** Géocodage inverse (lat/lon → adresse) via API Adresse data.gouv.fr */
export async function reverseGeocode(lat: number, lon: number): Promise<string> {
  const url = `https://api-adresse.data.gouv.fr/reverse/?lat=${lat}&lon=${lon}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Géocodage inverse indisponible');
  const data = await res.json();
  return data?.features?.[0]?.properties?.label || `${lat}, ${lon}`;
}

export interface RouteResult {
  /** Distance routière en km (arrondi à l'entier) */
  km: number;
  /** Durée du trajet en minutes (arrondi à l'entier) */
  minutes: number;
  /** Source: 'osrm' (route réelle) ou 'haversine' (vol d'oiseau, fallback) */
  source: 'osrm' | 'haversine';
}

/** Calcul distance routière + durée réelle via OSRM (route entre deux points) */
export async function calcRoute(from: Coords, to: Coords): Promise<RouteResult> {
  try {
    const url = `https://router.project-osrm.org/route/v1/driving/${from.lon},${from.lat};${to.lon},${to.lat}?overview=false`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('OSRM indisponible');
    const data = await res.json();
    const route = data?.routes?.[0];
    if (!route) throw new Error('Aucune route trouvée');
    return {
      km: Math.round(route.distance / 1000),
      minutes: Math.round(route.duration / 60),
      source: 'osrm',
    };
  } catch {
    // Fallback haversine + estimation 70 km/h
    const km = haversineKm(from, to);
    return {
      km: Math.round(km),
      minutes: Math.round((km / 70) * 60),
      source: 'haversine',
    };
  }
}

/** Calcul rapide depuis l'agence Cygnature vers un lieu */
export function routeFromAgency(to: Coords) {
  return calcRoute(AGENCE_CYGNATURE, to);
}

/** Formate une durée en minutes au format "1h25" ou "45min" */
export function formatDuree(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h00` : `${h}h${m.toString().padStart(2, '0')}`;
}
